import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "@/lib/router-compat";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import AuthModal from "@/components/AuthModal";
import LockedBlock from "@/components/subvenciones/LockedBlock";
import { EstadoBadge, FrescuraBadge, PrioridadBadge, TaxonomyPills } from "@/components/subvenciones/SubvencionCard";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/i18n/LanguageContext";
import { useLocalizedRoute } from "@/hooks/useLocalizedRoute";
import { useAuthState } from "@/hooks/useAuthState";
import { fetchSubvencionPublic, fetchSubvencionesFull } from "@/lib/subvencionesSsr";
import { useAdminGuestPreview } from "@/hooks/useAdminGuestPreview";
import { getSubvencionForAccount } from "@/lib/subvencionesPro.functions";
import { SITE_URL } from "@/lib/site-url";
import { resolveEnlace, type Subvencion } from "@/lib/subvenciones";
import { grantDetailHead, grantJsonLd, codigoFromParam, grantDetailPath } from "@/lib/subvencionesSeo";
import { ArrowLeft, ExternalLink } from "lucide-react";
import VerifiedBadge from "@/components/subvenciones/VerifiedBadge";

const Field = ({ label, value }: { label: string; value?: string | null }) => {
  if (!value) return null;
  return (
    <div>
      <p className="font-body text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-1 whitespace-pre-line font-body text-sm text-foreground">{value}</p>
    </div>
  );
};

const GrantDetail = () => {
  const { t, lang } = useLanguage();
  useLocalizedRoute();
  const params = useParams<{ codigo: string }>();
  const routeParam = params.codigo ?? "";
  const codigo = codigoFromParam(routeParam);
  const { isAuthenticated, isReady } = useAuthState();
  const guestPreview = useAdminGuestPreview(isAuthenticated);
  const [detailUnlocked, setDetailUnlocked] = useState(false);
  const [row, setRow] = useState<Subvencion | null | undefined>(undefined);
  const [authOpen, setAuthOpen] = useState(false);

  const basePath = lang === "es" ? "/recursos/subvenciones" : "/en/resources/subsidies";

  useEffect(() => {
    if (!isReady || !codigo) return;
    let cancelled = false;
    const run = async () => {
      // La ficha completa se desbloquea al crear cuenta gratuita/Lite. Las
      // funciones avanzadas del embudo siguen reservadas al Club Pro.
      if (isAuthenticated && !guestPreview) {
        // Si la función del servidor falla (p. ej. en el dominio público), se
        // lee la ficha directamente con la sesión: nunca se bloquea a un registrado.
        const full =
          (await getSubvencionForAccount({ data: { codigo } }).catch(() => null)) ??
          (await fetchSubvencionesFull().then((all) => all.find((s) => s.codigo === codigo) ?? null).catch(() => null));
        if (full) {
          if (!cancelled) {
            setDetailUnlocked(true);
            setRow(full);
          }
          return;
        }
      }
      const one = await fetchSubvencionPublic(codigo);
      if (!cancelled) {
        setDetailUnlocked(false);
        setRow(one);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [codigo, isAuthenticated, isReady, guestPreview]);

  // Los enlaces del catálogo ya generan el path canónico con el slug del título.
  const canonicalEs = row ? grantDetailPath("es", row) : `/subvenciones/${routeParam}`;
  const canonicalEn = row ? grantDetailPath("en", row) : `/en/grants/${routeParam}`;
  const canonicalPath = lang === "es" ? canonicalEs : canonicalEn;

  const jsonLd = useMemo(() => {
    if (!row) return undefined;
    return grantJsonLd(row, `${SITE_URL}${canonicalEs}`, lang);
  }, [row, canonicalEs, lang]);

  const seo = useMemo(() => (row ? grantDetailHead(lang, row) : null), [row, lang]);

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title={seo ? seo.title : t("grants", "heroTitle") + " — Retail Lemon"}
        description={seo ? seo.description : t("grants", "heroSubtitle")}
        path={canonicalPath}
        pathEs={canonicalEs}
        pathEn={canonicalEn}
        lang={lang}
        noindex={row === null}
        jsonLd={jsonLd}
      />
      <Header />

      <main className="mx-auto max-w-3xl px-5 pb-16 pt-6">
        <Link
          to={basePath}
          className="inline-flex items-center gap-1.5 font-body text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
          {t("grants", "backToHub")}
        </Link>

        {row === undefined ? (
          <div className="mt-6 space-y-4">
            <Skeleton className="h-8 w-3/4" />
            <Skeleton className="h-40 w-full rounded-xl" />
          </div>
        ) : row === null ? (
          <p className="mt-10 font-body text-sm text-muted-foreground">{t("grants", "noResults")}</p>
        ) : (
          <>
            <div className="mt-6 flex flex-wrap items-center gap-2">
              <EstadoBadge estado={row.estado} />
              <FrescuraBadge s={row} />
              <PrioridadBadge s={row} />
              <VerifiedBadge rows={[row]} />
            </div>

            <h1 className="mt-3 font-display text-2xl font-black leading-tight sm:text-3xl">{row.titulo}</h1>

            {!detailUnlocked && row.claim_publico && (
              <p className="mt-4 border-l-2 border-primary pl-4 font-body text-base text-foreground">
                {row.claim_publico}
              </p>
            )}

            <TaxonomyPills s={row} className="mt-4" />

            <h2 className="mt-8 font-display text-lg font-black sm:text-xl">{t("grants", "seoSheetTitle")}</h2>
            <div className="mt-4 grid grid-cols-1 gap-4 rounded-xl border border-foreground/10 bg-card p-5 sm:grid-cols-2">
              <Field label={t("grants", "organismo")} value={row.organismo} />
              <Field label={t("grants", "ambito")} value={row.ambito} />
              <Field label={t("grants", "modalidad")} value={row.modalidad} />
              <Field label={t("grants", "categoria")} value={row.categoria} />
            </div>

            <h2 className="mt-8 font-display text-lg font-black sm:text-xl">{t("grants", "seoAmountTitle")}</h2>
            <div className="mt-4 grid grid-cols-1 gap-4 rounded-xl border border-foreground/10 bg-card p-5 sm:grid-cols-2">
              <Field label={t("grants", "importe")} value={row.importe} />
              <Field label={t("grants", "presupuesto")} value={row.presupuesto} />
              <Field label={t("grants", "beneficiarios")} value={row.beneficiarios} />
            </div>

            <h2 className="mt-8 font-display text-lg font-black sm:text-xl">{t("grants", "seoRequirementsTitle")}</h2>
            <div className="mt-4">
              {detailUnlocked ? (
                <div className="space-y-4 rounded-xl border border-foreground/10 bg-card p-5">
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <Field label={t("grants", "fechaInicio")} value={row.fecha_inicio} />
                    <Field label={t("grants", "fechaFin")} value={row.fecha_fin} />
                  </div>
                  {(() => {
                    const link = resolveEnlace(row.enlace, { titulo: row.titulo, organismo: row.organismo });
                    return (
                      <div className="space-y-1.5">
                        <Button asChild variant="outline" className="gap-2">
                          <a href={link.href} target="_blank" rel="noopener noreferrer">
                            {link.kind === "official" ? t("grants", "enlaceCta") : t("grants", "searchOfficialCta")}
                            <ExternalLink className="h-4 w-4" strokeWidth={1.5} />
                          </a>
                        </Button>
                        {link.raw && (
                          <p className="font-body text-xs text-muted-foreground">
                            {t("grants", "sourceReference")}: {link.raw}
                          </p>
                        )}
                      </div>
                    );
                  })()}
                  <Field label={t("grants", "condiciones")} value={row.condiciones} />
                  <Field label={t("grants", "periodoGastos")} value={row.periodo_gastos} />
                  <Field label={t("grants", "mentorRecommendation")} value={row.notas} />
                  <p className="font-body text-xs text-muted-foreground">
                    <Link to={basePath} className="underline underline-offset-4">
                      {t("grants", "panelTitle")}
                    </Link>
                  </p>
                </div>
              ) : (
                <LockedBlock
                  labels={[t("grants", "plazos"), t("grants", "condiciones"), t("grants", "enlace")]}
                    title={t("grants", "detailGuestLockedTitle")}
                    body={t("grants", "detailGuestLockedBody")}
                    cta={t("grants", "guestRegisterCta")}
                    note={t("grants", "detailGuestLockedNote")}
                    onCta={() => setAuthOpen(true)}
                />
              )}
            </div>

            <div className="mt-8 space-y-1 font-body text-xs text-muted-foreground">
              {row.fecha_verificacion && (
                <p>
                  {t("grants", "verificado")} {row.fecha_verificacion}
                </p>
              )}
              {row.fuente && (
                <p>
                  {t("grants", "fuente")}: {row.fuente}
                </p>
              )}
              <p>{t("grants", "disclaimer")}</p>
            </div>
          </>
        )}
      </main>

      <Footer />

      <AuthModal
        open={authOpen}
        onOpenChange={setAuthOpen}
        initialMode="signup"
        intent={{ kind: "path", path: canonicalPath }}
        title={t("grants", "detailGuestLockedTitle")}
      />
    </div>
  );
};

export default GrantDetail;
